import AmountInput from "../components/AmountInput";
import { UNIT_TYPES } from "../constants";
import { muted, smallInput, t, tabular } from "../styles";
import { money, n, ronMoney } from "../utils";

const selectStyle = {
  minWidth: 0,
  fontSize: 13,
  padding: "8px 9px",
  border: "1px solid #dcdce3",
  borderRadius: 8,
  outline: "none",
  background: "#fff",
};

const RonLine = ({ show, value, rate, style }) =>
  show && rate ? (
    <span style={{ display: "block", fontSize: 11.5, fontWeight: 500, color: "#6f6f7c", marginTop: 2, ...tabular, ...style }}>
      {ronMoney(value, rate)}
    </span>
  ) : null;

const TotalLine = ({ label, value, showRon, rate, bold }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontSize: bold ? 14 : 13,
      fontWeight: bold ? 650 : 400,
      color: bold ? t.text : "#3c3c46",
      ...(bold ? { paddingTop: 6, borderTop: `1px solid ${t.border}` } : {}),
    }}
  >
    <span>{label}</span>
    <span style={{ textAlign: "right", ...tabular }}>
      {money(value)}
      <RonLine show={showRon} value={value} rate={rate} />
    </span>
  </div>
);

/**
 * Step 3: pick units (project -> phase -> block -> type, then search) and set
 * each one's settlement price and extras.
 */
const UnitsStep = ({ projects, f, setF, blockUnits, blockLoading, sel, onToggle, onUpdateSel, onRemove, totals, showRon, rate }) => {
  const project = projects.find((p) => String(p.id) === String(f.project));
  const phases = project?.phases || [];
  const phase = phases.find((p) => String(p.id) === String(f.phase));
  const blocks = phase?.blocks || [];

  const typesInBlock = UNIT_TYPES.filter((ut) => blockUnits.some((u) => u.type === ut.value));
  const q = f.q.trim().toLowerCase();
  const matches = blockUnits.filter((u) => u.type === f.type && (!q || `${u.label} ${u.meta} ${u.search}`.toLowerCase().includes(q)));
  const selIds = sel.map((s) => s.id);
  const available = matches.filter((u) => !u.sold).length;

  const hint = !f.project
    ? "Pick a project to start"
    : !f.phase
    ? "Pick a phase"
    : !f.block
    ? "Pick a block"
    : blockLoading
    ? "Loading units…"
    : !blockUnits.length
    ? "This block has no units"
    : !matches.length
    ? "No units match — clear the search or pick another type"
    : "";

  return (
    <div style={{ display: "flex", gap: 20, alignItems: "stretch", flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 420px", minWidth: 0, alignSelf: "flex-start", border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: 14, borderBottom: "1px solid #eeeef2", display: "flex", flexDirection: "column", gap: 12, background: t.surfaceSubtle }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8 }}>
            <select value={f.project} onChange={(e) => setF({ project: e.target.value, phase: "", block: "", q: "" })} style={selectStyle}>
              <option value="">Project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select value={f.phase} disabled={!project} onChange={(e) => setF({ phase: e.target.value, block: "", q: "" })} style={selectStyle}>
              <option value="">Phase</option>
              {phases.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select value={f.block} disabled={!phase} onChange={(e) => setF({ block: e.target.value, q: "" })} style={selectStyle}>
              <option value="">Block</option>
              {blocks.map((b) => (
                <option key={b.id} value={b.id}>
                  Block {b.name}
                </option>
              ))}
            </select>
            <select value={f.type} disabled={!f.block} onChange={(e) => setF({ type: e.target.value, q: "" })} style={selectStyle}>
              {(typesInBlock.length ? typesInBlock : UNIT_TYPES).map((ut) => (
                <option key={ut.value} value={ut.value}>
                  {ut.label}
                  {f.block ? ` (${blockUnits.filter((u) => u.type === ut.value).length})` : ""}
                </option>
              ))}
            </select>
          </div>
          {f.block && (
            <input
              value={f.q}
              onChange={(e) => setF({ q: e.target.value })}
              placeholder={f.type === "APT" ? "Search e.g. 12, 3 rooms, floor 2" : "Search number"}
              style={{ fontSize: 13, padding: "8px 11px", border: "1px solid #dcdce3", borderRadius: 8, outline: "none", background: "#fff" }}
            />
          )}
        </div>

        {hint ? (
          <div style={{ padding: "40px 20px", textAlign: "center", fontSize: 13, color: "#6f6f7c" }}>{hint}</div>
        ) : (
          <>
            <div style={{ maxHeight: 420, overflow: "auto" }}>
              {matches.map((u) => {
                const on = selIds.includes(u.id);
                const blocked = u.sold && !on;
                return (
                  <button
                    key={u.id}
                    type="button"
                    className="cw2-row"
                    disabled={blocked}
                    onClick={() => !blocked && onToggle(u)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "20px minmax(0,1fr) auto",
                      gap: 12,
                      alignItems: "center",
                      width: "100%",
                      textAlign: "left",
                      font: "inherit",
                      padding: "10px 14px",
                      border: "none",
                      borderBottom: "1px solid #f4f4f7",
                      background: on ? t.accentSoftBg : "#fff",
                      cursor: blocked ? "not-allowed" : "pointer",
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 5,
                        border: `1.5px solid ${on ? t.accent : blocked ? t.border : "#c3c3cf"}`,
                        background: on ? t.accent : "#fff",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {on ? "✓" : ""}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: blocked ? t.textMuted : t.text }}>{u.label}</span>
                      <span style={{ display: "block", ...muted, marginTop: 1 }}>{u.meta}</span>
                    </span>
                    <span style={{ textAlign: "right" }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: blocked ? t.textMuted : t.text, ...tabular }}>{money(u.price)}</span>
                      <RonLine show={showRon} value={u.price} rate={rate} />
                      <span
                        style={{
                          display: "block",
                          fontSize: 10.5,
                          fontWeight: 650,
                          letterSpacing: 0.3,
                          textTransform: "uppercase",
                          color: u.sold ? (on ? t.dangerText : t.textMuted) : on ? t.accent : t.success,
                          marginTop: 2,
                        }}
                      >
                        {on ? (u.sold ? "Selected · Sold" : "Selected") : u.sold ? "Sold" : "Available"}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ padding: "9px 14px", background: t.surfaceSubtle, borderTop: "1px solid #eeeef2", fontSize: 12, color: "#6f6f7c" }}>
              {matches.length} units · {available} available
            </div>
          </>
        )}
      </div>

      <div style={{ flex: "1 1 400px", minWidth: 0, minHeight: 380, position: "relative", border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: "none", padding: "13px 14px", borderBottom: "1px solid #eeeef2", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 650, flex: 1 }}>Selected units</span>
            <span style={{ fontSize: 12, color: "#6f6f7c" }}>{sel.length ? `${sel.length} ${sel.length === 1 ? "unit" : "units"}` : ""}</span>
          </div>
          {!sel.length ? (
            <div style={{ padding: "36px 20px", textAlign: "center", fontSize: 13, color: "#6f6f7c" }}>No units yet — tick units on the left.</div>
          ) : (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  flex: "none",
                  display: "grid",
                  gridTemplateColumns: "minmax(0,1fr) 128px 104px 30px",
                  gap: 10,
                  padding: "8px 14px",
                  background: t.surfaceSubtle,
                  borderBottom: "1px solid #eeeef2",
                  fontSize: 10.5,
                  fontWeight: 650,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: t.textMuted,
                }}
              >
                <span>Unit</span>
                <span>Settlement</span>
                <span>Extra</span>
                <span />
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {sel.map((s) => {
                  const u = s.unit;
                  const diff = n(s.settle) - u.price;
                  const note =
                    `List ${money(u.price)}` +
                    (Math.abs(diff) >= 0.01 && n(s.settle) > 0 ? (diff < 0 ? ` · ${money(-diff)} discount` : ` · ${money(diff)} above list`) : "");
                  return (
                    <div
                      key={s.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0,1fr) 128px 104px 30px",
                        gap: 10,
                        alignItems: "center",
                        padding: "10px 14px",
                        borderBottom: "1px solid #f4f4f7",
                        background: u.sold ? t.dangerBg : undefined,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13.5, fontWeight: 600 }}>{u.label}</span>
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 650,
                              letterSpacing: 0.3,
                              textTransform: "uppercase",
                              color: u.sold ? t.dangerText : t.success,
                            }}
                          >
                            {u.sold ? "Sold" : "Available"}
                          </span>
                        </div>
                        <div style={{ ...muted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.path}</div>
                        <div style={{ ...muted, color: diff < 0 && n(s.settle) > 0 ? "#8a6b12" : "#6f6f7c", marginTop: 1, ...tabular }}>{note}</div>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <AmountInput
                          value={s.settle}
                          onChange={(v) => onUpdateSel(s.id, { settle: v })}
                          style={smallInput(n(s.settle) <= 0 ? "#e3a09d" : "#dcdce3", { textAlign: "right", ...tabular })}
                        />
                        <RonLine show={showRon} value={n(s.settle)} rate={rate} style={{ textAlign: "right", paddingRight: 9, fontSize: 10.5 }} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <AmountInput
                          value={s.extra}
                          onChange={(v) => onUpdateSel(s.id, { extra: v })}
                          placeholder="0"
                          style={smallInput("#dcdce3", { textAlign: "right", ...tabular })}
                        />
                        <RonLine show={showRon} value={n(s.extra)} rate={rate} style={{ textAlign: "right", paddingRight: 9, fontSize: 10.5 }} />
                      </div>
                      <button
                        type="button"
                        className="cw2-danger-hover"
                        title="Remove unit"
                        onClick={() => onRemove(s.id)}
                        style={{ width: 30, height: 34, font: "inherit", fontSize: 13, borderRadius: 8, border: `1px solid ${t.border}`, background: "#fff", color: t.textMuted, cursor: "pointer" }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={{ flex: "none", padding: "12px 14px", background: t.surfaceSubtle, borderTop: "1px solid #eeeef2", display: "flex", flexDirection: "column", gap: 6 }}>
                <TotalLine label="Settlement total" value={totals.settle} showRon={showRon} rate={rate} />
                <TotalLine
                  label={
                    <>
                      Extras <span style={{ color: "#6f6f7c" }}>· invoiced as 1 separate payment</span>
                    </>
                  }
                  value={totals.extras}
                  showRon={showRon}
                  rate={rate}
                />
                <TotalLine label="Contract total" value={totals.total} showRon={showRon} rate={rate} bold />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitsStep;
